import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Typography,
  List,
  Avatar,
  Divider,
  Col,
  Descriptions,
  Space,
  Image,
  Button,
  message,
} from 'antd';
import { BookOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from '@refinedev/core';
import { EditButton, ListButton, RefreshButton, Show } from '@refinedev/antd';
import { endPoints } from '@/api/configs';
import useApi, { useGet } from '@/api/useApi';
import { GetOneClass } from '@/api/types/getOneClass';

const { Title } = Typography;

export const ShowClass = () => {
  const { id } = useParams<{ id: string }>();
  const { translate } = useTranslation();
  const { usePatch } = useApi();
  const { data, refetch } = useGet<GetOneClass>(
    endPoints.class(id),
    endPoints.class(id)
  );
  const [deletingStudentId, setDeletingStudentId] = useState<number | null>(
    null
  );
  const { mutate: deleteStudent } = usePatch(endPoints.class(id));
  if (!data?.data.data) {
    return <div>Class not found</div>;
  }
  const classData = data?.data.data;
  const handleDeleteStudent = (studentId: number) => {
    setDeletingStudentId(studentId);
    deleteStudent(
      {
        deleted_user_ids: [studentId],
      },
      {
        onSuccess: () => {
          message.success(translate('messages.success.delete'));
          refetch();
          setDeletingStudentId(null);
        },
        onError: (error) => {
          message.error(error.message);
          setDeletingStudentId(null);
        },
      }
    );
  };
  return (
    <Show
      title={
        <div className="flex items-center gap-3">
          <BookOutlined />
          <h1 className="text-2xl font-bold">{classData.name}</h1>
        </div>
      }
      isLoading={false}
      headerButtons={({ listButtonProps }) => (
        <>
          {listButtonProps && (
            <Space>
              <ListButton {...listButtonProps}>
                {translate('buttons.list')}
              </ListButton>
              <EditButton color="primary" type="primary" />
              <RefreshButton />
            </Space>
          )}
        </>
      )}
    >
      <Card>
        <div className="flex flex-col gap-4">
          <Col span={24}>
            <Title level={2}>{classData.name}</Title>
            <Divider />
          </Col>
          <Col span={24}>
            <Image
              src={`${import.meta.env.VITE_API_URL}uploads/${classData.banner_id}`}
              alt={classData.name}
            />
          </Col>
          <div className="flex flex-col gap-4">
            <Descriptions bordered column={1}>
              <Descriptions.Item
                label={`${translate('listing-classes.year')}:`}
                labelStyle={{
                  backgroundColor: 'transparent',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
              >
                {classData.year_range}
              </Descriptions.Item>
              <Descriptions.Item
                label={`${translate('listing-classes.description')}:`}
                span={2}
                labelStyle={{
                  backgroundColor: 'transparent',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
              >
                {classData.description}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {classData.students && (
            <Col span={24}>
              <Title level={4}>{translate('listing-classes.students')}</Title>
              <List
                grid={{ gutter: 16, column: 1 }}
                dataSource={classData.students}
                renderItem={(student) => (
                  <List.Item>
                    <Card size="small">
                      <List.Item.Meta
                        avatar={
                          student?.profile_photo_id ? (
                            <Avatar
                              src={`${import.meta.env.VITE_API_URL}uploads/${student?.profile_photo_id}`}
                              alt={student.name}
                            />
                          ) : (
                            <Avatar icon={<UserOutlined />} />
                          )
                        }
                        title={student?.name}
                        description={
                          <div className="flex flex-row justify-between">
                            <div className="flex flex-row gap-2">
                              <span>
                                {`${translate('listing-classes.id')}: ${student.profile_id}`}
                              </span>
                              {/* <span>
                                {`${translate('listing-users.gender.title')}: ${translate(
                                  `listing-users.gender.${student.gender}`
                                )}`}
                              </span> */}
                            </div>
                            <Button
                              className="border-red-500"
                              icon={<DeleteOutlined className="text-red-500" />}
                              size="small"
                              onClick={() =>
                                handleDeleteStudent(student.profile_id)
                              }
                              loading={deletingStudentId === student.profile_id}
                            />
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </Col>
          )}
        </div>
      </Card>
    </Show>
  );
};
